import React, {createContext, PropsWithChildren, useContext, useEffect, useState} from 'react';
import * as _ from 'lodash';
import {useHistory} from 'react-router-dom';
import {InspectionsMapAPI} from '../api';
import {AddressWithCoordinates} from '../types';

type InspectionsMapContext = {
  points: AddressWithCoordinates[]
  updatePoints: (
    fromLatitude: number,
    fromLongitude: number,
    toLatitude: number,
    toLongitude: number,
    fromDate?: string,
    toDate?: string,
    cityID?: number,
    streetID?: number,
  ) => Promise<void>,
  updateBounds: (
    fromLatitude: number,
    fromLongitude: number,
    toLatitude: number,
    toLongitude: number,
  ) => Promise<void>
}

const InspectionsMapContext = createContext<InspectionsMapContext>({
  points: [],
  updatePoints: async () => {},
  updateBounds: async () => {}
});

export const useInspectionsMapContext = () => useContext(InspectionsMapContext);

interface InspectionsMapProviderProps extends PropsWithChildren<any> {}

export const InspectionsMapProvider = ({children}: InspectionsMapProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState<AddressWithCoordinates[]>([]);
  const api = new InspectionsMapAPI();
  const history = useHistory();
  const qs = new URLSearchParams(window.location.search);
  const fromLatitude = parseFloat(qs.get('fromLatitude') || '');
  const fromLongitude = parseFloat(qs.get('fromLongitude') || '');
  const toLatitude = parseFloat(qs.get('toLatitude') || '');
  const toLongitude = parseFloat(qs.get('toLongitude') || '');
  const fromDate = qs.get('fromDate') || '';
  const toDate = qs.get('toDate') || '';
  const cityID = parseFloat(qs.get('cityID') || '-1');
  const streetID = parseFloat(qs.get('streetID') || '-1');

  useEffect(() => {
    setLoading(true);
    updatePoints(fromLatitude, fromLongitude, toLatitude, toLongitude, fromDate, toDate, cityID, streetID)
      .finally(() => setLoading(false));
  }, [fromLatitude, fromLongitude, toLatitude, toLongitude, fromDate, toDate, cityID, streetID]);

  const updatePoints = async (
    fromLatitude: number,
    fromLongitude: number,
    toLatitude: number,
    toLongitude: number,
    fromDate?: string,
    toDate?: string,
    cityID?: number,
    streetID?: number,
  ) => {
    try {
      if (_.isFinite(fromLatitude) && _.isFinite(fromLongitude) && _.isFinite(toLatitude) && _.isFinite(toLongitude)) {

        const result = await api.getInspectionsInsideBorders(
          fromLatitude,
          fromLongitude,
          toLatitude,
          toLongitude,
          fromDate,
          toDate,
          cityID,
          streetID
        );
        setPoints(result.list);
      } else {
        setPoints([]);
      }
    } catch (e) {
      setPoints([]);
    }
  };

  const updateBounds = async (
    fromLatitude: number,
    fromLongitude: number,
    toLatitude: number,
    toLongitude: number,
  ) => {
    const qs = new URLSearchParams(window.location.search);
    qs.set('fromLatitude', fromLatitude.toString());
    qs.set('fromLongitude', fromLongitude.toString());
    qs.set('toLatitude', toLatitude.toString());
    qs.set('toLongitude', toLongitude.toString());

    const path = history.location.pathname;
    history.replace(`${path}?${qs.toString()}`);
  };

  return (
    <InspectionsMapContext.Provider value={{points, updatePoints, updateBounds}}>
      {children}
    </InspectionsMapContext.Provider>
  );
};
